import React, { useCallback, useEffect, useRef } from "react";
import { FaChevronLeft, FaChevronRight, FaExclamationTriangle } from "react-icons/fa";
import {
  u2000ResolveTabIdAfterListKey,
  type U2000TabListOrientation,
} from "../../tools/u2000TabListKeyboardNavigation";

type TabType = {
  id: string;
  label: React.ReactNode;
  content: React.ReactNode;
  /** Libellé court dans le rail replié (orientation verticale + {@link tabListCollapsible}). */
  collapsedRailLabel?: string;
  /** Infobulle / aria-label du bouton rail replié (sinon {@link collapsedRailLabel}). */
  collapsedRailTitle?: string;
};

interface U2000TabProps {
  tabs: TabType[];
  defaultTab?: string;
  activeTab: string;
  setActiveTab: (id: string) => void;
  className?: string;
  /** Avec le parent en colonne flex (min-h-0), l’onglet actif occupe la hauteur restante (défilement interne aux panneaux). */
  fillRemainingHeight?: boolean;
  /** Masque le bandeau d’onglets (ex. mode Documents élargi BL-0488) tout en gardant le panneau actif monté. */
  hideTabList?: boolean;
  activeIndicator?: "dot" | "underline" | "border" | "icon" | "badge" | "pill";
  indicatorIcon?: React.ReactNode;
  indicatorBadge?: React.ReactNode;
  tabsWithErrors?: string[];
  /** Infobulle (et aria-label) de l’icône rouge sur un onglet en erreur. */
  tabValidationErrorTooltip?: string;
  /** Bandeau horizontal (défaut) ou panneau latéral vertical (BL-0673 U3129). */
  tabListOrientation?: U2000TabListOrientation;
  /** Classes additionnelles sur le tabpanel actif (sans remplacer le layout scroll). */
  tabPanelClassName?: string;
  /** Replier le bandeau d’onglets (orientation verticale uniquement). */
  tabListCollapsible?: boolean;
  tabListCollapsed?: boolean;
  onTabListCollapsedChange?: (collapsed: boolean) => void;
  expandTabListLabel?: string;
  collapseTabListLabel?: string;
}

const U2000Tab: React.FC<U2000TabProps> = ({
  tabs,
  defaultTab,
  activeTab,
  setActiveTab,
  className,
  fillRemainingHeight = false,
  hideTabList = false,
  activeIndicator = "underline",
  indicatorIcon,
  indicatorBadge,
  tabsWithErrors,
  tabValidationErrorTooltip = "Il y a des erreurs",
  tabListOrientation = "horizontal",
  tabPanelClassName,
  tabListCollapsible = false,
  tabListCollapsed = false,
  onTabListCollapsedChange,
  expandTabListLabel = "Show tabs",
  collapseTabListLabel = "Hide tabs",
}) => {
  const tabListRef = useRef<HTMLElement | null>(null);
  const isVertical = tabListOrientation === "vertical";
  const tabIds = tabs.map((tab) => tab.id);

  useEffect(() => {
    if (defaultTab) {
      const tab = tabs.find((tab) => tab.id === defaultTab);
      if (tab) {
        setActiveTab(tab.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);

  const focusTabButton = useCallback((tabId: string) => {
    const el = tabListRef.current?.querySelector<HTMLButtonElement>(`#tab-${tabId}`);
    el?.focus();
  }, []);

  const handleTabListKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const nextId = u2000ResolveTabIdAfterListKey(event.key, tabListOrientation, tabIds, activeTab);
      if (nextId == null || nextId === activeTab) {
        return;
      }
      event.preventDefault();
      setActiveTab(nextId);
      requestAnimationFrame(() => focusTabButton(nextId));
    },
    [activeTab, focusTabButton, setActiveTab, tabIds, tabListOrientation],
  );

  const rootLayoutClass = fillRemainingHeight
    ? isVertical
      ? "flex min-h-0 min-w-0 flex-1 flex-row gap-3 overflow-hidden"
      : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    : isVertical
      ? "flex flex-row gap-3"
      : "";

  const tabListCollapsibleActive = tabListCollapsible && isVertical && onTabListCollapsedChange != null;
  const tabListIsCollapsed = tabListCollapsibleActive && tabListCollapsed;

  const navClassName = isVertical
    ? tabListIsCollapsed
      ? "flex w-11 shrink-0 flex-col items-stretch gap-1 rounded-lg bg-gray-100 p-1"
      : "flex w-48 shrink-0 flex-col space-y-1 rounded-lg bg-gray-100 p-1"
    : "mb-4 flex shrink-0 space-x-1 rounded-lg bg-gray-100 p-1";

  const tabButtonClassName = (isActive: boolean) => {
    const base =
      "relative text-sm font-medium transition-all duration-200 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-1";
    const state = isActive ? "bg-white text-primary-700" : "text-gray-600 hover:text-gray-900 hover:bg-white/50";
    const layout = isVertical
      ? "flex min-h-11 w-full flex-none items-center justify-start px-3 py-2.5 text-left"
      : "flex-1 px-4 py-2.5";
    return `${base} ${state} ${layout}`;
  };

  const renderActiveIndicator = (isActive: boolean) => {
    if (!isActive) {
      return null;
    }
    const indicator = activeIndicator ?? "dot";
    if (indicator === "dot") {
      return isVertical ? (
        <div className="absolute top-1/2 right-2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-primary-600" />
      ) : (
        <div className="absolute bottom-0 left-1/2 h-1.5 w-1.5 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary-600" />
      );
    }
    if (indicator === "underline") {
      return isVertical ? (
        <div className="absolute top-1/4 bottom-1/4 right-0 w-0.5 rounded-xs bg-primary-600" />
      ) : (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-xs bg-primary-600" />
      );
    }
    if (indicator === "border") {
      return <div className="absolute left-0 top-1/4 bottom-1/4 w-1.5 rounded-r-sm bg-primary-600" />;
    }
    if (indicator === "badge" && indicatorBadge) {
      return (
        <span className="absolute -top-2 right-3 inline-flex items-center rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white">
          {indicatorBadge}
        </span>
      );
    }
    if (indicator === "pill") {
      return <div className="pointer-events-none absolute inset-0 m-0.5 rounded-full border-2 border-primary-500" />;
    }
    return null;
  };

  const tabPanelBaseClass = `animate-fade-in rounded-lg border border-gray-200 bg-white p-6 ${fillRemainingHeight ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto" : ""} ${isVertical && fillRemainingHeight ? "min-w-0" : ""} ${tabPanelClassName ?? ""}`.trim();

  return (
    <div className={`${rootLayoutClass} ${className ?? ""}`.trim()}>
      {!hideTabList ? (
        <nav
          ref={tabListRef}
          className={navClassName}
          role="tablist"
          aria-orientation={tabListOrientation}
          onKeyDown={handleTabListKeyDown}
        >
          {tabListCollapsibleActive ? (
            <button
              type="button"
              className="inline-flex h-9 w-full shrink-0 items-center justify-center rounded-md text-gray-600 hover:bg-white/70 hover:text-gray-900 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
              title={tabListIsCollapsed ? expandTabListLabel : collapseTabListLabel}
              aria-label={tabListIsCollapsed ? expandTabListLabel : collapseTabListLabel}
              aria-expanded={!tabListIsCollapsed}
              onClick={() => onTabListCollapsedChange?.(!tabListCollapsed)}
            >
              {tabListIsCollapsed ? (
                <FaChevronRight className="h-3.5 w-3.5" aria-hidden />
              ) : (
                <FaChevronLeft className="h-3.5 w-3.5" aria-hidden />
              )}
            </button>
          ) : null}
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            const railLabel = tab.collapsedRailLabel?.trim() || "•";
            const railTitle = tab.collapsedRailTitle?.trim() || railLabel;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.id}`}
                id={`tab-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                title={tabListIsCollapsed ? railTitle : undefined}
                className={
                  tabListIsCollapsed
                    ? `relative flex h-9 w-full shrink-0 items-center justify-center rounded-md text-xs font-semibold transition-all duration-200 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 ${
                        isActive ? "bg-white text-primary-700" : "text-gray-600 hover:bg-white/50 hover:text-gray-900"
                      }`
                    : tabButtonClassName(isActive)
                }
              >
                {activeIndicator === "icon" && isActive && indicatorIcon && !tabListIsCollapsed ? (
                  <span className="mr-2 inline-flex items-center">{indicatorIcon}</span>
                ) : null}

                {tabListIsCollapsed ? (
                  <span className="truncate px-0.5">{railLabel}</span>
                ) : (
                  <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">{tab.label}</span>
                )}

                {tabsWithErrors?.includes(tab.id) && (
                  <span
                    className={`inline-flex items-center ${tabListIsCollapsed ? "absolute -top-0.5 -right-0.5" : "ml-1.5"}`}
                    title={tabValidationErrorTooltip}
                    aria-label={tabValidationErrorTooltip}
                  >
                    <FaExclamationTriangle className="h-3 w-3 shrink-0 text-red-500" aria-hidden="true" />
                  </span>
                )}

                {!tabListIsCollapsed ? renderActiveIndicator(isActive) : null}
                {tabListIsCollapsed && isActive ? (
                  <div className="absolute top-1/4 bottom-1/4 left-0 w-0.5 rounded-r-sm bg-primary-600" aria-hidden />
                ) : null}
              </button>
            );
          })}
        </nav>
      ) : null}

      {tabs.map((tab) =>
        tab.id === activeTab ? (
          <div
            key={tab.id}
            role="tabpanel"
            id={`tabpanel-${tab.id}`}
            aria-labelledby={`tab-${tab.id}`}
            className={tabPanelBaseClass}
          >
            {tab.content}
          </div>
        ) : null,
      )}
    </div>
  );
};

export default U2000Tab;
